import { Close, Search } from "@mui/icons-material";
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import InstantQoute from "assets/icons/instant-quote.svg";
import {
  darkGreyColor,
  darkGreyColor100,
  headingColor,
  lightBlue100
} from "theme";
import BottomActionButtons from "components/BottomActionButtons/BottomActionButtons";
import NoCarrier from "pages/Application/NewApplication/InitiateApplication/components/NoCarrier";
import AnimatedLoader from "components/Loader/AnimatedLoader";
import { useAuth } from "hooks/useAuth";
import { AUTH_ROLES } from "constant/authRole";
import { ERROR_MESSAGE } from "constant/projectConstant";

import {
  useGenerateFormMutation,
  useGetAvailableCarriersQuery,
  useGetCatalogSettingsQuery,
  useUpdateApplicationDataMutation
} from "../../redux/modules/catalog";
import { useGetCountryStateQuery } from "../../redux/modules/config";

const SelectCarrier = ({
  setProductSelected,
  inputValue,
  selectedCard,
  setInputValue,
  setSelectedCard,
  industryId,
  setIndustryId,
  apiPayload,
  setApiPayload,
  setApplicationId,
  setScreen,
  productSelected,
  setApplicationData,
  setLoading,
  handleBack,
  setShowNoCarrier,
  applicationAPIData,
  applicationId,
  applicationData,
  addApplication,
  refetch,
  setOpenSnackbar,
  setSnackbarData
}) => {
  const [selectedState, setSelectedState] = useState("All");
  const [searchParams] = useSearchParams();
  const [carriers, setCarriers] = useState();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isApplicationCreated = searchParams.get("currentScreen");

  const [updateApplication] = useUpdateApplicationDataMutation();

  const { data: stateData } = useGetCountryStateQuery({ countryCode: "us" });

  const { data: availableCarriers, isFetching } = useGetAvailableCarriersQuery(
    {
      naicsCode: productSelected?.code,
      state: selectedState
    },
    { skip: !productSelected?.code, refetchOnMountOrArgChange: true }
  );

  const [generateForm] = useGenerateFormMutation();

  const [visibleCounts, setVisibleCounts] = useState(
    [] // Initialize with 4 items visible per carrier
  );
  const [showCarriers, setShowCarriers] = useState(false);

  const { data: catalogSettings } = useGetCatalogSettingsQuery(
    { token: user?.token, agencyId: user?.company?.companyId },
    { skip: !user }
  );
  useEffect(() => {
    if (catalogSettings) {
      setShowCarriers(
        catalogSettings?.allowCarriers === "Yes" ||
          (AUTH_ROLES.PORTAL_ADMIN === user?.role.slug || AUTH_ROLES.PORTAL_SUB_ADMIN === user?.role.slug)
          ? true
          : false
      );
    }
  }, [catalogSettings, user?.role]);

  useEffect(() => {
    if (availableCarriers) {
      setVisibleCounts(availableCarriers?.availableCarriers.map(() => 4));
      setApiPayload(availableCarriers?.availableCarriers);
      setIndustryId(availableCarriers?.availableCarriers[0]?.industryId);
      setCarriers(availableCarriers);
    }
  }, [availableCarriers]);

  useEffect(() => {
    if (isApplicationCreated && availableCarriers) {
      const findSelectedCarriers = availableCarriers?.availableCarriers?.map(
        (carrier) => {
          return {
            ...carrier,
            carriers: carrier?.carriers?.map((item) => {
              // Find the corresponding item in selectedCarrierDataBackend array
              const selectedCarrier =
                applicationAPIData?.application?.applicationData?.selectedCarrierDataBackend
                  ?.flatMap((data) => data.carriers) // Flattens all carriers from each selectedCarrierDataBackend element
                  ?.find(
                    (selected) =>
                      selected.carrierDetails?.catalogCompanyId ===
                        item.carrierDetails?.catalogCompanyId &&
                      selected.productId === item?.productId // Check for matching catalogProductId
                  );

              return {
                ...item,
                isSelected: selectedCarrier
                  ? selectedCarrier.isSelected
                  : false // Set isSelected based on matching item or default to false
              };
            })
          };
        }
      );

      setVisibleCounts(
        availableCarriers?.availableCarriers.map(
          (carrier) => carrier?.carriers?.length
        )
      );

      setApiPayload(findSelectedCarriers);
    }
  }, [isApplicationCreated, availableCarriers, applicationAPIData]);

  const handleViewMore = (index) => {
    setVisibleCounts((prev) => {
      const newCounts = [...prev];
      newCounts[index] += 4; // Show 4 more items for the specific carrier
      return newCounts;
    });
  };

  const handleChange = (e, catalogProductId, catalogCarrierAvailabilityId) => {
    const isChecked = e.target.checked;
    const updatedApiPayload = apiPayload.map((product) => {
      if (product.product.catalogProductId === catalogProductId) {
        return {
          ...product,
          carriers: product.carriers.map((carrier) => {
            if (!showCarriers || catalogSettings === undefined) {
              return { ...carrier, isSelected: isChecked };
            }
            return carrier.catalogCarrierAvailabilityId ===
              catalogCarrierAvailabilityId
              ? { ...carrier, isSelected: isChecked }
              : carrier;
          })
        };
      }
      return product;
    });

    setApiPayload(updatedApiPayload);
  };
  const isAnyCarrierSelected =
    Array.isArray(apiPayload) &&
    apiPayload.some((product) =>
      product.carriers.some((carrier) => carrier.isSelected)
    );

  const handleSubmit = () => {
    if (applicationAPIData?.application?.applicationData?.isDisabled) {
      navigate(
        `/start-application?currentScreen=CheckEligibility&applicationId=${applicationId}`
      );
    } else {
      const filteredPayloadData = apiPayload
        ?.filter((item) =>
          item?.carriers?.some((carrier) => carrier.isSelected === true)
        ) // Keep only items with at least one selected carrier
        .map((item) => ({
          ...item,
          carriers: item.carriers.filter(
            (carrier) => carrier.isSelected === true
          ) // Keep only selected carriers within each item
        }));
      setLoading(true);
      addApplication()
        .unwrap()
        .then((res) => {
          const processedData = {
            industryId,
            availableCarriers: filteredPayloadData,
            applicationId:
              applicationId ||
              res.result.application.catalogApplicationUuid ||
              ""
          };

          const payload = { processedData };
          setTimeout(() => {
            generateForm(payload)
              .then((res) => {
                if (
                  res?.data?.result?.form?.components[0]?.components?.length ===
                  0
                ) {
                  setOpenSnackbar(true);
                  setSnackbarData({
                    message: ERROR_MESSAGE.ELIGIBILITY_QUESTIONS_NOT_MAPPED,
                    severity: "error"
                  });
                  setLoading(false);

                  return;
                }
                setApplicationId(res.data?.result.applicationId);
                const payload = {
                  data: {
                    applicationId: res.data?.result.applicationId,
                    data: {
                      ...applicationData,
                      isDisabled: false,
                      answers: [],
                      industryId,
                      productSelected,
                      availableCarriers: filteredPayloadData,
                      formData: res?.data?.result?.form
                    }
                  }
                };
                updateApplication(payload)
                  .then(() => {
                    setApplicationData((prev) => {
                      return {
                        ...prev,
                        isDisabled: false,
                        answers: [],
                        industryId,
                        availableCarriers: filteredPayloadData,
                        formData: res?.data?.result?.form
                      };
                    });
                    // reload the page
                    refetch();
                    navigate(
                      `/start-application?currentScreen=CheckEligibility&applicationId=${res.data?.result?.applicationId}`,
                      { replace: true }
                    );
                    window.scrollTo(0, 0);
                    setLoading(false);
                  })
                  .catch((err) => {
                    setLoading(false);
                    setOpenSnackbar(true);
                    setSnackbarData({
                      message: err?.data?.message || ERROR_MESSAGE.SOMETHING_WENT_WRONG,
                      severity: "error"
                    });
                  });
              })
              .catch((err) => {
                setLoading(false);
                setOpenSnackbar(true);
                setSnackbarData({
                  message: err?.data?.message || ERROR_MESSAGE.SOMETHING_WENT_WRONG,
                  severity: "error"
                });
              });
          }, 500);
        })
        .catch((err) =>{
          setLoading(false);
          setOpenSnackbar(true);
          setSnackbarData({
            message: err?.data?.message || ERROR_MESSAGE.SOMETHING_WENT_WRONG,
            severity: "error"
          });
        });
    }
  };

  const columns = 4; // Number of items per row
  const totalItems = carriers?.availableCarriers?.length;
  const isInLastRow = (index) =>
    index >= Math.ceil(totalItems / columns - 1) * columns;

  const inLastColumn = (index) => index % columns === columns - 1;
  const inFirstColumn = (index) => index % columns === 0;

  const hasAvailableCarriers = Boolean(
    availableCarriers?.availableCarriers?.length
  );

  if (isFetching || (!carriers && availableCarriers === undefined)) {
    return (
      <div className="w-full h-[calc(100vh-200px)] flex items-center justify-center">
        <AnimatedLoader />
      </div>
    );
  }

  return (
    <>
      <Box
        className={`mt-3 p-6  bg-white rounded-[8px] w-full ${carriers?.availableCarriers ? "" : "h-[calc(100vh-160px)]"}  cursor-pointer`}
      >
        {hasAvailableCarriers ? (
          <>
            <Box className="flex w-full h-full rounded-[8px] border border-[#C4CAD3]">
              <input
                type="text"
                disabled={
                  applicationAPIData?.application?.applicationData?.isDisabled
                }
                className="w-full ring-0 outline-none max-h-[48px] py-3 pl-4 rounded-l-[8px] "
                placeholder="Search industry by NAICs code, Name or select from Popular Industries below"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              {selectedCard || inputValue ? (
                <button
                  className="h-auto w-[46px] bg-[#F6F8F9] rounded-[8px]"
                  onClick={() => {
                    if (
                      !applicationAPIData?.application?.applicationData
                        ?.isDisabled
                    ) {
                      setScreen("ProductList");
                      setSelectedCard(null);
                      setInputValue("");
                      setProductSelected(null);
                      navigate("/start-application", { replace: true });
                    }
                  }}
                >
                  <Close />
                </button>
              ) : (
                <button className="bg-[#3487F3] flex items-center rounded-l-none text-white rounded-[4px] px-4">
                  <Search />
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      color: "white",
                      fontWeight: 600
                    }}
                  >
                    Search
                  </Typography>
                </button>
              )}
            </Box>
            <Box className="mt-4 flex w-full justify-between items-center">
              <Typography
                sx={{
                  color: darkGreyColor100,
                  fontSize: "13px",
                  width: "40%"
                }}
              >
                Search Results:
              </Typography>
              <Box className="flex gap-4 w-[60%] justify-end  items-center ">
                <Box className="flex  gap-2 border-r border-r-[#E2E5E9] pr-4">
                  <Typography
                    sx={{
                      color: darkGreyColor100,
                      fontSize: "12px",
                      bgcolor: lightBlue100,
                      borderRadius: "4px",
                      px: "6px"
                    }}
                  >
                    E&S
                  </Typography>
                  <Typography
                    sx={{
                      color: darkGreyColor,
                      fontSize: "14px"
                    }}
                  >
                    Non Admitted
                  </Typography>
                </Box>
                <Box className="flex  gap-2 border-r border-r-[#E2E5E9] pr-4">
                  <img src={InstantQoute} alt="Instant Qoute" />
                  <Typography
                    sx={{
                      color: darkGreyColor,
                      fontSize: "14px"
                    }}
                  >
                    Instant Quote
                  </Typography>
                </Box>
                <Box className="flex w-[35%] gap-2  items-center justify-end ml-[6px]">
                  <Typography
                    sx={{
                      color: darkGreyColor,
                      fontSize: "14px",
                      pr: "-1rem",
                      width: "70%"
                    }}
                  >
                    Filter by State :
                  </Typography>
                  <FormControl
                    sx={{
                      width: 220,
                      height: "32px",
                      maxHeight: "32px",
                      padding: 0
                    }}
                    variant="outlined"
                  >
                    <TextField
                      size="small"
                      select
                      variant="outlined"
                      value={selectedState}
                      sx={{ p: 0 }}
                      InputProps={{
                        style: {
                          height: "32px",
                          fontSize: ".875rem",
                          fontWeight: 500
                        }
                      }}
                      InputLabelProps={{
                        style: {
                          height: "32px",
                          fontSize: ".875rem",
                          marginTop: "-2px"
                        }
                      }}
                      onChange={(e) => setSelectedState(e.target.value)}
                    >
                      {stateData?.map((option) => (
                        <MenuItem key={`state-${option}`} value={option}>
                          {option === "All" ? "All States" : option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormControl>
                </Box>
              </Box>
            </Box>
            <Box className="mt-3  border border-[#E7EBF0] grid rounded-[8px]">
              <Grid container spacing={0}>
                {apiPayload &&
                  apiPayload?.map((carrier, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      key={index}
                      sx={{
                        p: "1rem",
                        position: "relative",
                        minHeight: showCarriers ? "172px" : "32px",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          bottom: 0,
                          width: inLastColumn(index)
                            ? "93%"
                            : inFirstColumn(index)
                              ? "90%"
                              : "100%",
                          marginLeft: inFirstColumn(index) ? "10%" : "0",
                          marginRight: inLastColumn(index) ? "7%" : "0",
                          height: "1px", // Custom height for the "border"
                          backgroundColor: "#E1E6ED",
                          display: !isInLastRow(index) ? "block" : "none" // Only show if not in last row
                        }
                      }}
                    >
                      <Box
                        component="svg"
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: "16px", // Adds 10% margin from the top
                          bottom: "0", // Adds 10% margin from the bottom
                          height: "calc(100% - 32px)", // Custom height for the dotted border
                          width: "2px", // Adjust border width here
                          display: inFirstColumn(index) ? "none" : "block"
                        }}
                        viewBox={showCarriers ? "0 0 2 172" : "0 0 2 32"} // Adjust height as needed
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <line
                          x1="1.25"
                          y1="2.18557e-08"
                          x2="1.24999"
                          y2="172"
                          stroke="#C4CAD3"
                          strokeDasharray="2 2"
                        />
                      </Box>

                      {!showCarriers && (
                        <Box className="flex gap-0 ml-2 mt-2">
                          <label
                            className="container"
                            style={{
                              display: "flex",
                              alignItems: "center"
                            }}
                          >
                            <input
                              onChange={(e) =>
                                handleChange(
                                  e,
                                  carrier?.product?.catalogProductId,
                                  ""
                                )
                              }
                              type="checkbox"
                              checked={carrier?.carriers?.some(
                                (item) => item.isSelected
                              )}
                              disabled={
                                applicationAPIData?.application?.applicationData
                                  ?.isDisabled
                              }
                            />
                            <span className="checkmark"></span>
                            <Typography
                              fontSize=".875rem"
                              fontWeight={500}
                              color={headingColor}
                              ml={1.5}
                              sx={{ mt: -1 }} // Add some margin between the checkbox and the text
                            >
                              {carrier?.product?.name}
                            </Typography>
                          </label>
                        </Box>
                      )}
                      {!showCarriers ? (
                        <Typography
                          fontSize="13px"
                          color={headingColor}
                          sx={{ mt: -1, ml: 4.5 }} // Add some margin between the checkbox and the text
                        >
                          {carrier?.carriers?.length} Carriers
                        </Typography>
                      ) : (
                        <Typography
                          fontSize=".875rem"
                          fontWeight={500}
                          color={headingColor}
                          ml={1}
                        >
                          {carrier?.product?.name}

                          <div className="mt-3">
                            {carrier?.carriers
                              .slice(0, visibleCounts[index])
                              .map((item) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb:
                                      index !== carrier?.carriers.length &&
                                      "10px",
                                    cursor: "pointer",
                                    position: "relative"
                                  }}
                                  key={item.catalogCarrierAvailabilityId}
                                >
                                  <Box className="flex gap-0x -ml-2">
                                    <label
                                      className="container"
                                      style={{
                                        display: "flex",
                                        alignItems: "center"
                                      }}
                                    >
                                      <input
                                        onChange={(e) =>
                                          handleChange(
                                            e,
                                            carrier?.product?.catalogProductId,
                                            item.catalogCarrierAvailabilityId
                                          )
                                        }
                                        type="checkbox"
                                        checked={item?.isSelected}
                                        disabled={
                                          applicationAPIData?.application
                                            ?.applicationData?.isDisabled
                                        }
                                      />
                                      <span className="checkmark"></span>
                                      <Typography
                                        fontSize="13px"
                                        color={headingColor}
                                        sx={{ ml: 1.5, mt: -1 }} // Add some margin between the checkbox and the text
                                      >
                                        {item?.carrierDetails?.name}
                                      </Typography>
                                    </label>
                                  </Box>
                                  <Box className="flex gap-2 -mt-4">
                                    {!item?.isAdmitted ? (
                                      <Typography
                                        sx={{
                                          color: darkGreyColor100,
                                          fontSize: "12px",
                                          bgcolor: lightBlue100,
                                          borderRadius: "4px",
                                          px: "6px"
                                        }}
                                      >
                                        E&S
                                      </Typography>
                                    ) : null}
                                    {item?.isInstantQuote ? (
                                      <img
                                        src={InstantQoute}
                                        alt="Instant Qoute"
                                      />
                                    ) : null}
                                  </Box>
                                </Box>
                              ))}
                          </div>
                          {carrier?.carriers.length > visibleCounts[index] && (
                            <div
                              className="text-[12px] text-[#3487F3] font-[500] cursor-pointer -mt-[8px] -ml-[3px]"
                              onClick={() => handleViewMore(index)}
                            >
                              View{" "}
                              {Math.min(
                                4,
                                carrier?.carriers.length - visibleCounts[index]
                              )}{" "}
                              More
                            </div>
                          )}
                        </Typography>
                      )}
                    </Grid>
                  ))}
              </Grid>
            </Box>
            <BottomActionButtons
              backButtonTitle="Back"
              nextButtonTitle="Continue"
              isDisabled={!isAnyCarrierSelected}
              handleSubmit={() => handleSubmit()}
              handleBack={() => {
                if (
                  !applicationAPIData?.application?.applicationData?.isDisabled
                ) {
                  handleBack();
                }
              }}
            />
          </>
        ) : (
          <div className="w-full flex justify-center items-center h-full ">
            <NoCarrier
              setScreen={setScreen}
              setShowNoCarrier={setShowNoCarrier}
              setApplicationId={setApplicationId}
              applicationId={applicationId}
            />
          </div>
        )}
      </Box>
    </>
  );
};

export default SelectCarrier;
